import { useEffect, useState } from 'react';
import { funnelStep } from '../analytics/funnel';
import { checkoutOffers, creditBalance, startCheckout, type CheckoutOffers, type CreditBalance } from '../api/billing';
import { PRIVACY_URL, TERMS_URL } from '@/config/legal';
import {
  checkoutOffered,
  freeWeekEnd,
  offerFor,
  rememberCheckoutOffer,
  trialCheckoutAwaited,
  type Offer,
} from '../state/trial';
import { personalOfferLines, rememberOfferTerms, trialEndFromNow, type PersonalOffer, type PlanId } from '../state/plans';
import PlanChoice from './new/PlanChoice';
import { useSession } from '../state/useSession';
import { Button, Screen, SectionLabel } from '../ui';
import RedeemCode, { type Redeemed } from './account/RedeemCode';

/** What one top-up adds, when the server's answer leaves it out. The server's table decides. */
const TOPUP_CREDITS = 10000;

/**
 * The browser buys credits through Stripe Checkout, never RevenueCat.
 *
 * src/payments/webCheckout.ts was written for exactly this client — its header names
 * app.saddle-sites.com — because react-native-purchases is a native module and an IAP
 * button in a browser is a dead end. This also keeps the web builder outside the app's
 * in-app-purchase surface entirely.
 */
export default function Credits() {
  const [credits, setCredits] = useState<number | null>(null);
  const [tier, setTier] = useState<string>('free');
  /*
   * Whether a store is BILLING this account — the server's `subscribed`, never the tier. A promo
   * code (CREDITS4MARTI: 1,000 credits and the paid tier) sets the tier with no plan behind it,
   * and deciding on the tier offered those people the subscribers' top-up, or told them their
   * credits "refill when your plan renews", and never the plan itself.
   */
  const [subscribed, setSubscribed] = useState(false);
  /*
   * The trial, or — one per account (the server, 2026-09-29) — the plan from day one. "Start 7-day
   * free trial" over a checkout that charges on the spot promised a week the account had had.
   */
  const [offer, setOffer] = useState<Offer>('trial');
  /** A running free week's end: when its trial charges, rather than 14 days from now. */
  const [trialEndsAt, setTrialEndsAt] = useState<string | undefined>(undefined);
  /*
   * Whether the server SAID the trial is still this account's — true only on its yes. `offer`
   * cannot stand in for it: it is 'trial' until the balance says otherwise, and 'trial' for a
   * balance that says nothing, so a refusal of the top-up promised a used trial (review of F23,
   * 2026-09-30). Unknown is no.
   */
  const [trialAvailable, setTrialAvailable] = useState(false);
  const known = credits !== null;
  /*
   * Whether the balance question has been answered, either way. Nothing is offered for sale
   * until it has: a subscriber must not be shown Subscribe for the moment before their tier
   * arrives — that button is how they used to buy a second plan.
   */
  const [settled, setSettled] = useState(false);
  const [offers, setOffers] = useState<CheckoutOffers>({ topup: { available: false } });
  /*
   * And whether what is on sale has been answered. The subscriber's row is a button or a line
   * depending on it, and drawn before the answer it showed "refill when your plan renews" and
   * then swapped it for "Buy 10,000 credits".
   */
  const [offersKnown, setOffersKnown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ownerId = useSession().user?.id ?? '';
  /*
   * Back from Stripe with a trial the account does not show yet (state/trial.ts). The success page
   * says the credits "can take a minute" and links here; in that minute this screen still read the
   * free tier and offered the trial again, and one press made a second one. Decided from the
   * record before the balance answers too: a read that fails must not offer it either.
   */
  const [awaited, setAwaited] = useState(() => trialCheckoutAwaited(ownerId, {}) !== null);

  /** A price made for this account (2026-11-01), shown above the plans while it has none. */
  const [personal, setPersonal] = useState<PersonalOffer | null>(null);

  /** The offer taken: that plan, its price applied by the server, charged today. */
  async function takeOffer(o: PersonalOffer): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      rememberCheckoutOffer('subscribe', { plan: o.plan });
      rememberOfferTerms(personalOfferLines(o).terms);
      funnelStep('checkout_started', { offer: 'subscribe' }, { now: true });
      const result = await startCheckout('subscription', undefined, { plan: o.plan });
      if (result.status === 'signin_required') setError('Please sign in first.');
      else if (result.status === 'error') setError(result.message);
    } finally {
      setBusy(false);
    }
  }

  function show(b: CreditBalance): void {
    setCredits(b.creditsRemaining);
    setTier(b.tier);
    setSubscribed(b.subscribed === true);
    setOffer(offerFor(b));
    setTrialEndsAt(freeWeekEnd(b));
    setTrialAvailable(b.trialAvailable === true);
    setAwaited(trialCheckoutAwaited(ownerId, b) !== null);
    setPersonal(b.personalOffer ?? null);
  }

  /** Asked again by hand while the account catches up with a trial. */
  async function checkAgain(): Promise<void> {
    setBusy(true);
    try {
      await creditBalance().then(show).catch(() => undefined);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    /*
     * Ask what the balance actually IS. A store starting at zero would tell someone they
     * have none before they had spent anything — which is what the phone used to do.
     * A failure leaves it unknown, and unknown shows "—" rather than a wrong number.
     */
    void creditBalance()
      .then(show)
      .catch(() => undefined)
      .finally(() => setSettled(true));
    // Whether a top-up is on sale at all; "no" until the server says otherwise.
    void checkoutOffers()
      .then(setOffers)
      .finally(() => setOffersKnown(true));
  }, []);

  /** A code's answer carries the new balance, so the number above changes without a reload. */
  function redeemed(result: Redeemed): void {
    if (typeof result.creditsRemaining === 'number') setCredits(result.creditsRemaining);
    if (result.tier) setTier(result.tier);
  }

  /** `plan`: a subscription, the plan chosen (PlanChoice); absent, the subscriber's top-up. */
  async function buy(plan?: PlanId): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      // Which offer and plan this was, for the page Stripe sends them back to (state/trial.ts).
      rememberOfferTerms(null);
      if (plan) rememberCheckoutOffer(offer, offer === 'trial' ? { plan, chargeAt: trialEndsAt ?? trialEndFromNow().toISOString() } : { plan });
      // Counted like the reveal's and the plan's (2026-11-01): every subscription checkout begun is in the journey log.
      if (plan) funnelStep('checkout_started', { offer }, { now: true });
      // Whether the trial may be named if a top-up is refused for want of a plan (F23).
      const result = plan
        ? await startCheckout('subscription', undefined, { plan })
        : await startCheckout('topup', undefined, { trialAvailable });
      if (result.status === 'signin_required') setError('Please sign in before buying credits.');
      else if (result.status === 'error') {
        setError(result.message);
        /*
         * A subscriber from before the server recorded which store bills them reads as not
         * subscribed until the server asks Stripe, which it does exactly here — and remembers. So
         * the refusal is also the answer: read the balance again, and the top-up takes the place
         * of the Subscribe button that was just refused.
         */
        if (result.reason === 'already_subscribed') {
          await creditBalance().then(show).catch(() => undefined);
        }
      }
      // 'redirecting' navigates the browser away; nothing left to do.
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen title="Credits">
      <p className="type-display credits-balance">
        {tier === 'unlimited' ? 'Unlimited' : known ? credits : '—'}
      </p>
      <p className="type-body project-meta">
        {tier === 'unlimited'
          ? 'This account has no limit.'
          : 'Credits are spent when Saddle builds or changes an app.'}
      </p>

      {error && <p role="alert" data-error-code="checkout" className="type-caption field-error-text">{error}</p>}

      {/*
        One thing to buy, and which one depends on who is buying. "Get more credits" sold
        everybody the subscription, so a subscriber whose month ran out bought a SECOND
        plan. A subscriber — somebody a store is billing, not merely the paid tier — is offered
        the one-time top-up, only while the server says it is on sale, and told plainly
        otherwise. Everybody else, the promo's paid tier included, is offered the trial; a
        balance that could not be read offers it too, and the server refuses a subscriber a
        second plan and says so, in words.
      */}
      {/*
        The free tier is gone (the owner, 2026-09-25). Since 2026-10-27 what is sold is a plan —
        Single, Monthly, or Pro by conversation — with the 14-day trial while the account can have
        one: the same choice the reveal shows (PlanChoice), so the words cannot drift. Account's
        "Choose a plan" leads here.
      */}
      {settled && tier !== 'unlimited' && !subscribed && !awaited && personal && (
        <div className="card clay-card personal-offer" role="region" aria-label="Your offer">
          <p className="type-heading">{personalOfferLines(personal).title}</p>
          <Button title={personalOfferLines(personal).button} onPress={() => void takeOffer(personal)} loading={busy} />
          <p className="type-caption plan-detail">{personalOfferLines(personal).terms}</p>
        </div>
      )}
      {settled && tier !== 'unlimited' && !subscribed && !awaited && (
        <>
          <SectionLabel>{personal ? 'Or choose a plan' : 'Choose a plan'}</SectionLabel>
          <PlanChoice offer={offer} trialEndsAt={trialEndsAt} busy={busy} onChoose={(plan) => void buy(plan)} />
        </>
      )}
      {settled && tier !== 'unlimited' && !subscribed && awaited && (
        <>
          <p role="status" className="type-caption project-meta">
            Stripe has your {checkoutOffered() === 'subscribe' ? 'subscription' : 'trial'}. It shows here once your account catches up with it — usually within a minute.
          </p>
          <Button title="Check again" variant="secondary" onPress={() => void checkAgain()} loading={busy} />
        </>
      )}
      {settled && offersKnown && tier !== 'unlimited' && subscribed && offers.topup.available && (
        <Button
          title={`Buy ${(offers.topup.credits ?? TOPUP_CREDITS).toLocaleString()} credits`}
          onPress={() => void buy()}
          loading={busy}
        />
      )}
      {settled && offersKnown && tier !== 'unlimited' && subscribed && !offers.topup.available && (
        <p className="type-caption project-meta">Your credits refill when your plan renews.</p>
      )}

      {/* The phone's Account → Credits field. On the web it sits here too, next to the balance
          it changes: the iOS app no longer offers it (App Store 3.1.1), so a code given to
          somebody on an iPhone is redeemed in a browser. */}
      <SectionLabel>Redeem code</SectionLabel>
      <RedeemCode tier={known ? tier : null} onRedeemed={redeemed} />

      <p className="type-caption legal-links">
        <a href={TERMS_URL} target="_blank" rel="noreferrer">Terms</a>
        {' · '}
        <a href={PRIVACY_URL} target="_blank" rel="noreferrer">Privacy Policy</a>
      </p>
    </Screen>
  );
}
